import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { commafy, swal } from '../../../utils/methods';
import { _post } from '../../../utils/network';
import { toShamsiDate } from '../courseUtils';

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb.component';
import FormField from '../../../components/Form/FormField/FormField.component';
import LoadingButton from '../../../components/LoadingButton/LoadingButton.component';
import ListTable from '../../../components/List/ListTable/ListTable.component';
import Pagination from '../../../components/List/Pagination/Pagination.component';

const COURSE_OPTIONS_COUNT = 200;

const CourseEnrollmentsList = () => {
	const { t } = useTranslation();
	const [urlParams] = useSearchParams();
	const [enrollments, setEnrollments] = useState([]);
	const [courseOptions, setCourseOptions] = useState([]);
	const [totalCount, setTotalCount] = useState(0);
	const [searchButtonIsLoading, setSearchButtonIsLoading] = useState(false);
	const [revokingId, setRevokingId] = useState(null);
	const [searchParams, setSearchParams] = useState({
		course_id: urlParams.get('course_id') || null,
		phone_number: null,
		page: 1,
		count: 10
	});

	const breadcrumbItems = [
		{ title: t('dashboard'), link: '/dashboard' },
		{ title: t('courses'), link: '/courses-list' },
		{ title: t('course_enrollments') }
	];

	const formatPayment = payment => {
		if(!payment || !Object.keys(payment).length)
			return '-';
		const parts = [];
		const sim = Number(payment.sim_paid || 0) - Number(payment.sim_change || 0);
		const zar = Number(payment.zar_paid || 0);
		if(zar)
			parts.push(`${commafy(zar)} ${t('score2_name')}`);
		if(sim)
			parts.push(`${commafy(Math.round(sim * 100) / 100)} ${t('score1_name')}`);
		return parts.join(' + ') || '-';
	};

	const revokeEnrollment = enrollment => {
		swal(async () => {
			setRevokingId(enrollment.id);
			const response = await _post('/courses/admin/deleteCourseEnrollment', { id: enrollment.id, refund: true });
			setRevokingId(null);
			switch(response.code){
				case 200:
					toast.success(response[t('api_message')]);
					setSearchParams(current => ({ ...current }));
					break;
				default:
					toast.error(response[t('api_message')]);
			}
		}, {
			text: t('course_revoke_confirm'),
			confirmButtonText: t('yes'),
			cancelButtonText: t('no')
		});
	};

	const enrollmentsListData = useMemo(() => ({
		head: [t('purchase_date'), t('fullname'), t('mobile'), t('course'), t('amount'), t('operation')],
		body: enrollments.map(enrollment => [
			toShamsiDate(enrollment.created_at),
			enrollment.user_fullname,
			enrollment.phone_number,
			<Link to={`/courses-edit/${enrollment.course_id}`}>{enrollment.course_title}</Link>,
			formatPayment(enrollment.payment),
			<LoadingButton className="btn btn-danger btn-sm p-1" type="button" name={`revoke-${enrollment.id}`} isLoading={revokingId === enrollment.id} onClick={() => revokeEnrollment(enrollment)}>
				<i className="fa fa-undo"></i> {t('course_revoke_and_refund')}
			</LoadingButton>
		]),
		directions: ['ltr', '', 'ltr', '', '', 'ltr']
	}), [enrollments, revokingId, t]);

	const getCourseOptions = async () => {
		const response = await _post('/courses/admin/getCourses', { page: 1, count: COURSE_OPTIONS_COUNT });
		if(response.code !== 200)
			return toast.error(response[t('api_message')]);
		setCourseOptions(response.data.courses.map(course => ({ key: course.title, value: course.id })));
	};

	const getEnrollments = async () => {
		setSearchButtonIsLoading(true);
		const response = await _post('/courses/admin/getCourseEnrollments', searchParams);
		setSearchButtonIsLoading(false);
		switch(response.code){
			case 200:
				setTotalCount(response.data.course_enrollments_count);
				setEnrollments(response.data.course_enrollments);
				break;
			default:
				toast.error(response[t('api_message')]);
		}
	};

	const filterEnrollments = event => {
		event.preventDefault();
		if(searchButtonIsLoading)
			return;
		const formData = new FormData(event.target);
		setSearchParams({
			...searchParams,
			course_id: formData.get('searchEnrollmentCourse') || null,
			phone_number: formData.get('searchEnrollmentPhone')?.trim() || null,
			page: 1
		});
	};

	useEffect(() => {
		getCourseOptions();
	}, []);
	useEffect(() => {
		getEnrollments();
	}, [searchParams]);

	return (
		<>
			<Breadcrumb items={breadcrumbItems} />
			<div className="card mb-3">
				<div className="card-header">
					<h5 className="mb-0">{t('filter')} {t('course_enrollments')}</h5>
				</div>
				<div className="card-body bg-light">
					<form onSubmit={filterEnrollments} key={courseOptions.length}>
						<div className="row gx-2">
							<FormField type="select" fieldKey="searchEnrollmentCourse" title={t('course')} value={searchParams.course_id} selectItems={courseOptions} isSearch />
							<FormField fieldKey="searchEnrollmentPhone" title={t('mobile')} value={searchParams.phone_number} isSearch ltr />
						</div>
						<div>
							<LoadingButton className="btn btn-primary d-inline-block mt-3 me-2" type="submit" name="search" isLoading={searchButtonIsLoading}>
								<i className="fa fa-search"></i> {t('search')}
							</LoadingButton>
						</div>
					</form>
				</div>
			</div>
			<ListTable title={t('course_enrollments')} pageNumber={searchParams.page} pageCounter={searchParams.count} totalCount={totalCount} data={enrollmentsListData} />
			<Pagination pagesCount={Math.ceil(totalCount / searchParams.count)} action={page => setSearchParams({ ...searchParams, page })} />
		</>
	);
};

export default CourseEnrollmentsList;
